import React, { useState, useEffect } from 'react';
import { auth, db } from '../../firebase/config';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
// import './Steps.css';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';

interface Step3ExperienceProps {
  onComplete: (step: string) => void;
}

interface Category {
  id: string;
  name_en: string;
  name_es: string;
  description_en?: string;
  description_es?: string;
}

interface Skill {
  id: string;
  name_en: string;
  name_es: string;
  categoryId: string;
  description_en?: string;
  description_es?: string;
}

const Step3Experience: React.FC<Step3ExperienceProps> = ({ onComplete }) => {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadCategoriesAndSkills();
  }, []);

  const loadCategoriesAndSkills = async () => {
    try {
      setLoading(true);
      if (!auth.currentUser) {
        setError(t('onboarding.skills.errorLoading'));
        setLoading(false);
        return;
      }
      // Obtener el sector del usuario
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      let sector = 'construction';
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.sector === 'health') {
          sector = 'health';
        }
      }
      // Seleccionar el documento de skills/certificaciones según el sector
      const docId = sector === 'health' ? 'Health_skills_certs' : 'skills_and_certs';
      const skillsAndCertsDoc = await getDoc(doc(db, 'Skills_and_certifications', docId));
      if (!skillsAndCertsDoc.exists()) {
        logger.warn('Step3Experience', 'No se encontraron datos de habilidades y certificaciones para el sector', sector);
        setCategories([]);
        setSkills([]);
        setError(t('onboarding.skills.errorLoading'));
        setLoading(false);
        return;
      }
      const data = skillsAndCertsDoc.data();
      logger.info('Step3Experience', 'Datos cargados:', data);
      if (!data.skills || !data.categories) {
        logger.warn('Step3Experience', 'Datos incompletos en el documento');
        setCategories([]);
        setSkills([]);
        setError(t('onboarding.skills.errorLoading'));
        setLoading(false);
        return;
      }
      // Soportar tanto arrays como mapas
      const allSkills = Array.isArray(data.skills) ? data.skills : Object.values(data.skills || {});
      const allCategories = Array.isArray(data.categories) ? data.categories : Object.values(data.categories || {});
      logger.info('Step3Experience', 'Categorías cargadas:', allCategories);
      logger.info('Step3Experience', 'Habilidades cargadas:', allSkills);
      // Filtrar solo las categorías que tienen habilidades asociadas
      const categoriesWithSkills = allCategories.filter((category: Category) => 
        allSkills.some((skill: Skill) => skill.categoryId === category.id)
      );
      logger.info('Step3Experience', 'Categorías filtradas:', categoriesWithSkills);
      setCategories(categoriesWithSkills);
      setSkills(allSkills);
      setError('');
    } catch (error) {
      logger.error('Step3Experience', 'Error al cargar categorías y habilidades', error);
      setError(t('onboarding.skills.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSkillToggle = (skillId: string) => {
    setSelectedSkills(prev => 
      prev.includes(skillId)
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!auth.currentUser) {
        throw new Error('Usuario no autenticado');
      }

      const selectedSkillsData = selectedSkills.map(skillId => {
        const skill = skills.find(s => s.id === skillId);
        return {
          id: skillId,
          name_en: skill?.name_en || '',
          name_es: skill?.name_es || '',
          categoryId: skill?.categoryId || ''
        };
      });

      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        experience: {
          skills: selectedSkillsData,
          updatedAt: new Date().toISOString()
        }
      });

      logger.info('Step3Experience', 'Experiencia guardada exitosamente');
      onComplete('experience');
    } catch (error: any) {
      logger.error('Step3Experience', 'Error al guardar experiencia', error);
      setError(error.message || t('onboarding.skills.errorSaving'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-8 flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="text-red-600 bg-red-100 border border-red-200 rounded-lg px-6 py-4 text-center">
          {error}
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="text-gray-600 bg-gray-100 border border-gray-200 rounded-lg px-6 py-4 text-center">
          {t('onboarding.skills.noData')}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-3xl mx-auto p-8 bg-white rounded-[20px_40px_20px_40px] shadow-2xl border border-white/40 animate-fadeIn font-['Montserrat']">
      <h2 className="text-3xl font-bold text-red-600 mb-4 text-center">{t('onboarding.skills.title')}</h2>
      <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
        {t('onboarding.skills.description')}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-8 max-w-3xl mx-auto">
        <div className="w-full grid grid-cols-1 gap-6">
          {categories.map((category) => (
            <div key={category.id} className="col-span-1 flex flex-col gap-3 w-full items-center">
              <button
                type="button"
                className={`w-full max-w-md p-4 rounded-xl font-semibold transition-all duration-300 text-left mx-auto ${
                  selectedCategories.includes(category.id)
                    ? 'bg-red-500 text-white shadow-lg transform -translate-y-1'
                    : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-red-300 hover:shadow-md'
                }`}
                onClick={() => handleCategoryToggle(category.id)}
              >
                {i18n.language === 'en' ? category.name_en : category.name_es}
              </button>
              {selectedCategories.includes(category.id) && (
                <div className="w-full max-w-md grid grid-cols-1 gap-2 pl-2 mx-auto">
                  {skills
                    .filter(skill => skill.categoryId === category.id)
                    .map((skill) => (
                      <button
                        key={skill.id}
                        type="button"
                        className={`w-full block px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-left ${
                          selectedSkills.includes(skill.id)
                            ? 'bg-blue-500 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        onClick={() => handleSkillToggle(skill.id)}
                      >
                        {i18n.language === 'en' ? skill.name_en : skill.name_es}
                      </button>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {selectedSkills.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <p className="text-blue-800 font-semibold mb-4">
              {t('onboarding.skills.selectedSkills', { count: selectedSkills.length })}
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedSkills.map(skillId => {
                const skill = skills.find(s => s.id === skillId);
                return (
                  <span 
                    key={skillId} 
                    className="px-3 py-1 bg-blue-500 text-white rounded-full text-sm font-medium"
                  >
                    {i18n.language === 'en' ? skill?.name_en : skill?.name_es}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        <button 
          type="submit" 
          disabled={loading || selectedSkills.length === 0}
          className="w-full p-4 bg-red-500 text-white rounded-xl font-semibold transition-all duration-300 hover:bg-red-600 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
        >
          {loading ? t('common.saving') : t('common.saveAndContinue')}
        </button>
      </form>
    </div>
  );
};

export default Step3Experience; 